import Vue from 'vue';
import { createAuth0Client } from '@auth0/auth0-spa-js';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

// Détecter si on est sur une plateforme native (iOS/Android)
// Capacitor v3+ : Utiliser isNativePlatform() qui fonctionne correctement
const isNativePlatform = Capacitor.isNativePlatform();

// Gérer les deep links pour Auth0 sur mobile
if (isNativePlatform) {
  console.log('[Auth0] Registering appUrlOpen listener for deep links');
  App.addListener('appUrlOpen', async (event) => {
    console.log('[Auth0] appUrlOpen event received:', event);
    const url = event.url;
    console.log('[Auth0] Deep link URL:', url);
    
    // Vérifier si c'est un callback Auth0
    if (url.includes('code=') && url.includes('state=')) {
      console.log('[Auth0] Auth0 callback detected in deep link');
      try {
        // Extraire les paramètres de l'URL
        const urlObj = new URL(url);
        const code = urlObj.searchParams.get('code');
        const state = urlObj.searchParams.get('state');
        console.log('[Auth0] Extracted code and state from deep link');
        
        // Fermer le browser natif immédiatement
        console.log('[Auth0] Closing native browser');
        await Browser.close();
        
        // Vérifier le state
        const savedState = sessionStorage.getItem('auth0_state');
        if (state !== savedState) {
          console.error('[Auth0] State mismatch!');
          return;
        }
        
        // Récupérer le code_verifier
        const codeVerifier = sessionStorage.getItem('auth0_code_verifier');
        const redirectPath = sessionStorage.getItem('auth0_redirect_path') || '/';
        
        console.log('[Auth0] Exchanging code for token...');
        
        // Échanger le code contre un token en appelant /oauth/token
        const authConfig = await import('../../auth_config.json');
        const tokenResponse = await fetch(`https://${authConfig.domain}/oauth/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            grant_type: 'authorization_code',
            client_id: authConfig.clientId,
            code: code,
            code_verifier: codeVerifier,
            redirect_uri: 'com.voxdubsas.app://callback'
          })
        });
        
        if (!tokenResponse.ok) {
          const error = await tokenResponse.text();
          console.error('[Auth0] Token exchange failed:', error);
          return;
        }
        
        const tokens = await tokenResponse.json();
        console.log('[Auth0] Token exchange successful');
        
        // Décoder l'ID token pour extraire les informations utilisateur
        try {
          const idToken = tokens.id_token;
          const base64Url = idToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          
          const user = JSON.parse(jsonPayload);
          console.log('[Auth0] User decoded from token:', user);
          
          // Mettre à jour l'instance Auth0 actuelle
          const authInstance = getInstance();
          if (authInstance) {
            authInstance.isAuthenticated = true;
            authInstance.user = user;
            console.log('[Auth0] Updated current auth instance');
          }
          
          // Nettoyer les données temporaires
          sessionStorage.removeItem('auth0_code_verifier');
          sessionStorage.removeItem('auth0_state');
          sessionStorage.removeItem('auth0_redirect_path');
          
          // Rediriger vers la page demandée SANS recharger
          console.log('[Auth0] Redirecting to:', redirectPath);
          window.location.href = `#${redirectPath}`;
          
        } catch (e) {
          console.error('[Auth0] Error decoding token:', e);
        }
        
      } catch (e) {
        console.error('[Auth0] Error processing deep link:', e);
      }
    } else {
      console.log('[Auth0] Deep link is not an Auth0 callback');
    }
  });
  console.log('[Auth0] appUrlOpen listener registered');
}

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = isNativePlatform ? 'com.voxdubsas.app://callback' : window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(options, config) {
        console.log('[Auth0] loginWithPopup called with options:', options);
        this.popupOpen = true;

        try {
          console.log('[Auth0] Opening popup...');
          await this.auth0Client.loginWithPopup(options, config);
          console.log('[Auth0] Popup closed, getting user...');
          this.user = await this.auth0Client.getUser();
          console.log('[Auth0] User from popup:', this.user);
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          console.log('[Auth0] isAuthenticated from popup:', this.isAuthenticated);
          this.error = null;
        } catch (e) {
          this.error = e;
          console.error('[Auth0] Error in loginWithPopup:', e);
          console.error('[Auth0] Error message:', e.message);
          console.error('[Auth0] Error stack:', e.stack);
        } finally {
          this.popupOpen = false;
        }

        // Double-check user is loaded
        console.log('[Auth0] Double-checking user...');
        this.user = await this.auth0Client.getUser();
        console.log('[Auth0] Final user:', this.user);
        
        // Si getUser() ne fonctionne pas, essayer getIdTokenClaims()
        if (!this.user) {
          console.log('[Auth0] getUser() returned null, trying getIdTokenClaims()...');
          const claims = await this.auth0Client.getIdTokenClaims();
          console.log('[Auth0] ID Token claims:', claims);
          if (claims) {
            this.user = claims;
            console.log('[Auth0] User set from claims');
          }
        }
        
        this.isAuthenticated = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          console.log('handle redirect user',this.user)
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      console.log('[Auth0] Initializing Auth0 client...');
      console.log('[Auth0] Platform:', Capacitor.getPlatform());
      console.log('[Auth0] isNativePlatform:', isNativePlatform);
      console.log('[Auth0] Redirect URI:', redirectUri);
      
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        ...options,
        connection: "email",
        authorizationParams: {
          redirect_uri: redirectUri,
          ...options.authorizationParams
        }
      });

      console.log('[Auth0] Client created');

      try {
        // If the user is returning to the app after authentication.. 
        // Sur native (hash mode), on cherche dans le hash
        // Sur web, on cherche dans search
        let urlParams;
        if (isNativePlatform) {
          // Sur native, vérifier dans le hash
          urlParams = window.location.hash;
          console.log('[Auth0] Checking hash for callback:', urlParams);
          
          // Si le hash contient code et state, préparer pour Auth0 SDK
          if (urlParams.includes('code=') && urlParams.includes('state=')) {
            // Auth0 SDK s'attend à avoir les params dans window.location.search
            // On va créer une URL temporaire pour que SDK puisse les lire
            const hashParams = urlParams.substring(urlParams.indexOf('?'));
            console.log('[Auth0] Hash params:', hashParams);
            // Créer un objet URLSearchParams pour parser
            const params = new URLSearchParams(hashParams.substring(1));
            const code = params.get('code');
            const state = params.get('state');
            console.log('[Auth0] Found code and state in hash');
            
            // Mettre temporairement dans search pour Auth0 SDK
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('code', code);
            currentUrl.searchParams.set('state', state);
            // Utiliser history.replaceState pour éviter de recharger
            window.history.replaceState({}, '', currentUrl.toString());
            urlParams = window.location.search;
            console.log('[Auth0] Moved params to search for SDK:', urlParams);
          }
        } else {
          urlParams = window.location.search;
        }
        
        console.log('[Auth0] Final URL params to check:', urlParams);
        
        if (
          urlParams.includes('code=') &&
          urlParams.includes('state=')
          
          ) {
            console.log('[Auth0] Found auth callback in URL, handling redirect...');
            // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          this.error = null;

          console.log('[Auth0] Redirect handled successfully, appState:', appState);

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }else{
          console.log('[Auth0] No auth callback in URL, checking existing auth state...');
          // check if user is authenticated
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          console.log('[Auth0] Is authenticated:', this.isAuthenticated);
          if(this.isAuthenticated){
            this.user = await this.auth0Client.getUser();
            console.log('[Auth0] User loaded:', this.user);
          }

        }
      } catch (e) {
        console.error('[Auth0] Error during initialization:', e);
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();
        console.log('[Auth0] Final auth state - isAuthenticated:', this.isAuthenticated, 'user:', this.user);
        // get user metadata
        this.loading = false;
        console.log('[Auth0] Loading complete');
      }
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};